import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { LogisticsCatalogs, TransfersService, errMsg } from '../logistics.service';

/**
 * Paso 7-8: el material llega al destino. Se palomea (o escanea) lo que sí
 * llegó; lo que no, queda como faltante y se avisa a mesa de control. Al
 * confirmar, el sistema descuenta del origen y asigna al destino.
 */
@Component({
  selector: 'app-transfer-receive-dialog',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule, MatIconModule,
    MatCheckboxModule, MatProgressSpinnerModule],
  styleUrl: '../logistics-dialog.scss',
  styles: [`.wrap { display: grid; gap: 10px; min-width: min(680px, 86vw); }`],
  template: `
<h2 mat-dialog-title>Recibir {{ t.folio }}</h2>
<mat-dialog-content>
  <div class="wrap">
    <div class="hint-box"><mat-icon>place</mat-icon>
      <span>De <b>{{ cat.warehouseLabelById(t.originWarehouse) }}</b> a <b>{{ cat.warehouseLabelById(t.destinationWarehouse) }}</b>
      {{ t.dhlGuide ? ' · Guía DHL ' + t.dhlGuide : '' }}</span></div>
    <div class="scan-row">
      <mat-form-field appearance="outline" subscriptSizing="dynamic">
        <mat-label>Escanear etiqueta para confirmar</mat-label>
        <mat-icon matPrefix>qr_code_scanner</mat-icon>
        <input matInput [(ngModel)]="scan" autocomplete="off" (keydown.enter)="$event.preventDefault(); onScan()">
      </mat-form-field>
      <span class="counter">{{ received.size }} de {{ items.length }} recibidas</span>
    </div>
    @if (scanMsg) { <div class="hint-box hint-box--danger"><mat-icon>error</mat-icon><span>{{ scanMsg }}</span></div> }
    <div class="pieces">
      <table>
        <thead><tr>
          <th><mat-checkbox color="primary" [checked]="received.size === items.length" (change)="all($event.checked)"></mat-checkbox></th>
          <th>Código</th><th>Descripción</th><th>Lote</th><th>Etiqueta</th>
        </tr></thead>
        <tbody>
          @for (it of items; track it.stock) {
            <tr [class.is-on]="received.has(it.stock)" [class.is-flag]="!received.has(it.stock)">
              <td><mat-checkbox color="primary" [checked]="received.has(it.stock)" (change)="toggle(it.stock)"></mat-checkbox></td>
              <td>{{ it.code }}</td><td>{{ it.description }}</td><td>{{ it.batch || '—' }}</td><td class="tag">{{ it.tagRfid || '—' }}</td>
            </tr>
          }
        </tbody>
      </table>
    </div>
    @if (received.size < items.length) {
      <div class="hint-box hint-box--warn"><mat-icon>warning</mat-icon>
        <span>{{ items.length - received.size }} pieza(s) quedarán como <b>faltantes</b> y se avisará a mesa de control.</span></div>
    }
  </div>
</mat-dialog-content>
<mat-dialog-actions align="end">
  <button mat-button (click)="ref.close(false)" [disabled]="saving()">Cancelar</button>
  <button mat-flat-button color="primary" (click)="confirm()" [disabled]="saving() || !received.size">
    @if (saving()) {<mat-spinner diameter="18"></mat-spinner>} @else {<mat-icon>move_to_inbox</mat-icon>}
    Confirmar recepción
  </button>
</mat-dialog-actions>
  `,
})
export class TransferReceiveDialogComponent {
  readonly t = inject<{ transfer: any }>(MAT_DIALOG_DATA).transfer;
  readonly ref = inject(MatDialogRef<TransferReceiveDialogComponent>);
  readonly cat = inject(LogisticsCatalogs);
  private readonly service = inject(TransfersService);
  private readonly msg = inject(CustomMessageService);

  readonly saving = signal(false);
  readonly items: any[] = (this.t.items ?? []).filter((i: any) => i.stock);
  readonly received = new Set<string>(this.items.map(i => i.stock));
  scan = '';
  scanMsg = '';

  toggle(id: string): void { this.received.has(id) ? this.received.delete(id) : this.received.add(id); }
  all(on: boolean): void { this.items.forEach(i => on ? this.received.add(i.stock) : this.received.delete(i.stock)); }

  /** Escanear marca la pieza como recibida (la primera vez desmarca las demás). */
  private scanned = false;
  onScan(): void {
    const tag = this.scan.trim().toUpperCase(); this.scan = '';
    if (!tag) return;
    const it = this.items.find(i => String(i.tagRfid ?? '').toUpperCase() === tag);
    if (!it) { this.scanMsg = `La etiqueta ${tag} no viene en este traspaso.`; return; }
    this.scanMsg = '';
    if (!this.scanned) { this.received.clear(); this.scanned = true; }
    this.received.add(it.stock);
  }

  async confirm(): Promise<void> {
    this.saving.set(true);
    try {
      const r = await this.service.receive(this.t.id, [...this.received]);
      await this.msg.open({ title: '¡Recibido!', type: r?.missing ? 'warning' : 'success', duration: 3000,
        message: `Traspaso <b>${this.t.folio}</b>: ${r?.received ?? this.received.size} pieza(s) en el destino` +
                 (r?.missing ? `, ${r.missing} faltante(s).` : '. Inventario actualizado.') });
      this.ref.close(true);
    } catch (err) {
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudo registrar la recepción.'), dismissBtn: true });
    } finally {
      this.saving.set(false);
    }
  }
}
